import { useCustomToast } from 'design-system';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { BulkUpsertFamilyRow } from 'pages/inquiry-processor/components/master-product-families/editable-families-grid/editable-family-row.types';
import { useQueryClient } from 'react-query';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { masterProductFamiliesEndpoint } from './use-get-master-product-families-api';

const endpoint = `${masterProductFamiliesEndpoint}/bulk`;
export const bulkUpsertMasterProductFamiliesEndpointIdentifier = endpoint;

export interface BulkUpsertMasterProductFamiliesResultDto {
  created: number;
  updated: number;
}

export interface BulkUpsertMasterProductFamiliesErrorDto {
  statusCode?: number;
  message: string;
  errors?: string[];
}

type TData = { data: BulkUpsertMasterProductFamiliesResultDto };
type TError = AxiosError<BulkUpsertMasterProductFamiliesErrorDto>;
type TVariables = { rows: BulkUpsertFamilyRow[] };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/**
 * Bulk create/update master product families via POST .../master-product-families/bulk.
 * Rows with an `id` update that family; rows without one create a new family (which
 * server-side also gets an auto-created "Generic" starter variant).
 * All-or-nothing: a 400 carries per-row messages in `errors` and nothing is written.
 */
export function useBulkUpsertMasterProductFamiliesApi(options?: Options): ReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();

  const mutationFn = async ({ rows }: TVariables): Promise<TData> => {
    try {
      const { data } = await http.post(endpoint, { rows });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [endpoint],
    onSuccess: (data: TData) => {
      queryClient.invalidateQueries([masterProductFamiliesEndpoint]);
      const { created, updated } = data.data;
      toast({ description: `Saved: ${created} created, ${updated} updated`, status: 'success' });
    },
    ...options,
  });
}
